import { memo, useEffect, useRef, useState } from 'react'
import cn from 'classnames'

import { Text } from '~/shared/ui/Text'

import { BIG_PERCENT_ANIMATION_MS } from './config'
import type { BigPercentProps } from './types'

import styles from './BigPercent.module.scss'

/** Крупный процент (сбываемость, совместимость, натальная карта) — спека 01, компонент 32. */
export const BigPercent = memo(function BigPercent(props: BigPercentProps) {
  const { value, size = 'M', isAnimated = false } = props
  const [displayValue, setDisplayValue] = useState(isAnimated ? 0 : value)
  const frameRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (!isAnimated) {
      setDisplayValue(value)
      return
    }

    const start = performance.now()

    const tick = (now: number) => {
      const progress = Math.min(1, (now - start) / BIG_PERCENT_ANIMATION_MS)
      setDisplayValue(Math.round(value * progress))

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick)
      }
    }

    frameRef.current = requestAnimationFrame(tick)

    // Страховка: `requestAnimationFrame` не приходит, пока вкладка в фоне (а экран результата
    // как раз открывают из шаринга и возвращаются к нему позже). Без неё процент оставался
    // «0%» до первого кадра — то есть выглядел посчитанным и нулевым.
    const fallback = setTimeout(() => setDisplayValue(value), BIG_PERCENT_ANIMATION_MS + 100)

    return () => {
      if (frameRef.current !== undefined) cancelAnimationFrame(frameRef.current)
      clearTimeout(fallback)
    }
  }, [value, isAnimated])

  return (
    <span className={cn(styles.root, styles[`size_${size}`])}>
      <Text variant='HERO_PERCENT' color='ACCENT'>{`${displayValue}%`}</Text>
    </span>
  )
})
